import React, {memo, useEffect, FC, ReactNode, useMemo} from "react";
import {useDispatch, useSelector} from "react-redux";
import {reviewSelector} from "./review.selector";
import {usePageTitle} from "../hooks/usePageTitle";
import {CardMarkupViewer} from "../markup/cardMarkupViewer/cardMarkupViewer";
import {ReviewState} from "./review.interface";
import {SubmitReviewButtons} from "./submitReviewButtons/submitReviewButtons";
import {ShowAnswerButton} from "./showAnswerButton/showAnswerButton";
import {ReviewActionBar} from "./actionBar/reviewActionBar";
import {SideMenu} from "./sideMenu/sideMenu";
import {NavigationProps} from "../utils/navigationProps";
import {reviewActions} from "./review.slice";
import styled from "styled-components";
import { ThemeProps } from "../styles/theme";
import { CompletedScreen } from "./completedScreen/completedScreen";
import { SubcardView } from "./subcardView/subcardView";
import { MULTICARD_BUTTON_TEXT } from "./constants/multicard.constants";

interface ReviewProps extends NavigationProps {
    cardId?: string;
    children?: ReactNode;
}

const Root = styled.div`
    position: fixed;
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
`;
Root.displayName = 'ReviewRoot';

const Content = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
`;
Content.displayName = 'ReviewContent';

const CardContainer = styled.div<ThemeProps>`
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    background-color: ${props => props.theme.reviewCardBackground};
    overflow: auto;
    padding: 20px 0;
    
    /* This ensures the container can scroll when needed */
    min-height: 0;
    
    /* Create a flex context for the card wrapper */
    & > div {
        display: flex;
        flex-direction: column;
        min-height: 100%;
        justify-content: center;
        padding: 0 20px;
    }
`;
CardContainer.displayName = 'ReviewCardContainer';

const Card = styled.div<ThemeProps>`
    display: flex;
    flex-direction: column;
    background-color: ${props => props.theme.primaryBackground};
    max-width: 80%;
    margin: 0 auto;
    border-radius: 4px;
    padding: 15px;
    box-sizing: border-box;
    border: ${props => props.theme.primaryBorder} 2px solid;
    box-shadow: 4px 4px ${props => props.theme.cardShadow};
    
    /* Allow card content to be scrollable if needed */
    overflow: visible;
    
    /* Ensure images don't overflow */
    img {
        max-width: 100%;
        height: auto;
    }
`;
Card.displayName = 'ReviewCard';

// Buttons container to ensure buttons stay at the bottom
const ButtonsContainer = styled.div`
    position: relative;
    z-index: 1;
`;
ButtonsContainer.displayName = 'ButtonsContainer';

const SubcardResultButtons = styled.div`
    display: flex;
    justify-content: center;
    gap: 16px;
    margin: 10px auto;
`;
SubcardResultButtons.displayName = 'SubcardResultButtons';

const SubcardResultButton = styled.button`
    padding: 8px 20px;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
    color: #ffffff;
`;

const WrongButton = styled(SubcardResultButton)`
    background-color: ${props => props.theme.dangerColor || '#dc3545'};
    border: 1px solid ${props => props.theme.dangerColor || '#dc3545'};

    &:hover {
        opacity: 0.9;
    }
`;
WrongButton.displayName = 'WrongButton';

const RightButton = styled(SubcardResultButton)`
    background-color: ${props => props.theme.successColor || '#28a745'};
    border: 1px solid ${props => props.theme.successColor || '#28a745'};

    &:hover {
        opacity: 0.9;
    }
`;
RightButton.displayName = 'RightButton';

const FinishCardButton = styled.button`
    display: block;
    margin: 8px auto 0;
    padding: 8px 20px;
    border-radius: 4px;
    background-color: ${props => props.theme.neutralButtonBackground};
    color: ${props => props.theme.neutralButtonForeground};
    border: 1px solid ${props => props.theme.neutralButtonBorder};
    cursor: pointer;
    font-family: inherit;
    transition: all 0.2s ease;

    &:hover {
        background-color: ${props => props.theme.neutralButtonBorder};
    }
`;
FinishCardButton.displayName = 'FinishCardButton';

export const Review: FC<ReviewProps> = memo((props) => {
    const stateProps = useSelector(reviewSelector);

    const { current, total } = stateProps;
    const pageTitle = useMemo(() => {
        if (current !== undefined && current > 0 && total !== undefined && total > 0)
            return `Review ${current}/${total} · Memkeep`;
        return 'Review · Memkeep';
    }, [current, total]);
    usePageTitle(pageTitle);

    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(reviewActions.initialize(props.cardId));
    }, [dispatch, props.cardId])

    return (
        <Root>
            <Content>
                <ReviewActionBar>{props.children}</ReviewActionBar>

                {stateProps.reviewState === ReviewState.Completed ? (
                    <CompletedScreen />
                ) : (
                    <>
                        {stateProps.markupState !== undefined && (
                            <CardContainer>
                                <div>
                                    <Card data-testid="review-card">
                                        {/* If this is a multicard in question or answer state, show the current subcard */}
                                        {Boolean(stateProps.isMultiCard) && Boolean(stateProps.currentSubcard) && 
                                         (stateProps.reviewState === ReviewState.SubcardQuestion || 
                                          stateProps.reviewState === ReviewState.SubcardAnswer) ? (
                                            <SubcardView
                                                cardId={stateProps.cardId}
                                                currentSubcard={stateProps.currentSubcard}
                                                reviewState={stateProps.reviewState}
                                                markupState={stateProps.markupState}
                                                cardMarkup={stateProps.markup}
                                            />
                                        ) : (
                                            <CardMarkupViewer
                                                cardId={stateProps.cardId}
                                                markup={stateProps.markup!}
                                                state={stateProps.markupState!}
                                                expandReference={true}/>
                                        )}
                                    </Card>
                                </div>
                            </CardContainer>
                        )}
                        <ButtonsContainer>
                            {/* Standard review buttons for regular cards */}
                            {stateProps.isMultiCard === false && (stateProps.reviewState === ReviewState.OnlyQuestion
                                || stateProps.reviewState === ReviewState.QuestionAndHint) && (
                                <ShowAnswerButton/>
                            )}
                            {stateProps.isMultiCard === false && stateProps.reviewState === ReviewState.Answer && (
                                <SubmitReviewButtons/>
                            )}
                            
                            {/* MultiCard specific buttons */}
                            {Boolean(stateProps.isMultiCard) && stateProps.reviewState === ReviewState.SubcardQuestion && (
                                <ShowAnswerButton />
                            )}
                            {Boolean(stateProps.isMultiCard) && stateProps.reviewState === ReviewState.SubcardAnswer && (
                                <>
                                    <SubcardResultButtons>
                                        <WrongButton data-testid="subcard-wrong" onClick={() => dispatch(reviewActions.markSubcardResult(false))}>
                                            {MULTICARD_BUTTON_TEXT.GOT_IT_WRONG}
                                        </WrongButton>
                                        <RightButton data-testid="subcard-right" onClick={() => dispatch(reviewActions.markSubcardResult(true))}>
                                            {MULTICARD_BUTTON_TEXT.GOT_IT_RIGHT}
                                        </RightButton>
                                    </SubcardResultButtons>
                                    <FinishCardButton data-testid="subcard-finish" onClick={() => dispatch(reviewActions.finishSubcards())}>
                                        {MULTICARD_BUTTON_TEXT.FINISH_CARD}
                                    </FinishCardButton>
                                </>
                            )}
                            {/* Whole-card difficulty rating once the subcard loop is done. */}
                            {Boolean(stateProps.isMultiCard) && stateProps.reviewState === ReviewState.Answer && (
                                <SubmitReviewButtons/>
                            )}
                        </ButtonsContainer>
                    </>
                )}
            </Content>
            <SideMenu />
        </Root>
    );
});
Review.displayName = 'Review';
